/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Box } from "@mui/material";
import DisplayAmount from "../../shared/DisplayAmount";
import { ImSpinner8 } from "react-icons/im";

ModuleRegistry.registerModules([AllCommunityModule]);

type RowData = {
  asset: string;
  available: string;
  pending: string;
  total: string;
  crypto: boolean;
};

const AccountBalancesTab: React.FC<{
  data: any;
  isFetchingBalance: boolean;
}> = ({ data, isFetchingBalance }) => {
  const [balancesData, setBalancesData] = useState<any>(null);
  const [fiatData, setFiatData] = useState<RowData[]>([]);
  const [cryptoData, setCryptoData] = useState<RowData[]>([]);

  useEffect(() => {
    setBalancesData(data);

    if (data && Array.isArray(data)) {
      const fiat = data.filter((item: RowData) => !item.crypto);
      const crypto = data.filter((item: RowData) => item.crypto);

      setFiatData(fiat);
      setCryptoData(crypto);
    }
  }, [data]);

  const AvailableRenderer = (params: ICellRendererParams) => (
    <DisplayAmount
      amount={Number(params.data.available)}
      currency={params.data.asset}
    />
  );

  const PendingRenderer = (params: ICellRendererParams) => (
    <DisplayAmount
      amount={Number(params.data.pending)}
      currency={params.data.asset}
    />
  );

  const TotalRenderer = (params: ICellRendererParams) => (
    <DisplayAmount
      amount={Number(params.data.total)}
      currency={params.data.asset}
    />
  );

  const columns: ColDef[] = [
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      flex: 1,
    },
    {
      field: "available",
      headerName: "Available",
      sortable: false,
      flex: 1,
      cellRenderer: AvailableRenderer,
    },
    {
      field: "pending",
      headerName: "Pending",
      sortable: false,
      flex: 1,
      cellRenderer: PendingRenderer,
    },
    {
      field: "total",
      headerName: "Total",
      sortable: false,
      flex: 1,
      cellRenderer: TotalRenderer,
    },
  ];

  const columnsCrypto: ColDef[] = [
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      flex: 1,
    },
    {
      field: "available",
      headerName: "Available",
      sortable: false,
      flex: 1,
    },
    {
      field: "pending",
      headerName: "Pending",
      sortable: false,
      flex: 1,
    },
    {
      field: "total",
      headerName: "Total",
      sortable: false,
      flex: 1,
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  if (isFetchingBalance || !balancesData) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-7">
        <ImSpinner8
          className="spinner mt-1 text-primary-500"
          style={{ fontSize: "1.8rem" }}
        />
      </div>
    );
  }

  if (
    !isFetchingBalance &&
    (!fiatData || fiatData.length === 0) &&
    (!cryptoData || cryptoData.length === 0)
  ) {
    return (
      <div className="p-7">
        <p className="py-8 text-center text-sm text-gray-500">
          No available balance
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-8">
      {fiatData && fiatData.length > 0 && (
        <Box mb={4}>
          <div className="mb-2 text-sm font-semibold text-navy-700">
            Fiat Balances
          </div>
          <div style={{ width: "100%" }}>
            <AgGridReact
              theme={themeAlpine}
              rowData={fiatData}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              getRowId={(params) => params.data.asset}
              rowHeight={48}
              headerHeight={44}
              domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
              suppressPaginationPanel={true}
              overlayNoRowsTemplate="No data available"
            />
          </div>
        </Box>
      )}
      {cryptoData && cryptoData.length > 0 && (
        <Box>
          <div className="mb-2 text-sm font-semibold text-navy-700">
            Wallet Balances
          </div>
          <div style={{ width: "100%" }}>
            <AgGridReact
              theme={themeAlpine}
              rowData={cryptoData}
              columnDefs={columnsCrypto}
              defaultColDef={defaultColDef}
              getRowId={(params) => params.data.asset}
              rowHeight={48}
              headerHeight={44}
              domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
              suppressPaginationPanel={true}
              overlayNoRowsTemplate="No data available"
            />
          </div>
        </Box>
      )}
    </div>
  );
};

export default AccountBalancesTab;
